import React, { useState, useEffect, useRef } from 'react';
import './SupportWidget.css';
import { getSupportGreeting } from '../supportGreetingTranslations';
import { API_URL } from '../apiBase';
import { usePaymentSessionId } from '../paymentSession';
import {
  defaultSupportMessages,
  loadSupportMessages,
  saveSupportMessages,
  mergeSupportMessages,
} from '../supportChatStorage';

const DEFAULT_GREETING = defaultSupportMessages()[0].text;

export default function SupportWidget({ sessionId, countryCode }) {
  const effectiveSessionId = usePaymentSessionId(sessionId);
  const [supportChatOpen, setSupportChatOpen] = useState(false);
  const [supportMessages, setSupportMessages] = useState(defaultSupportMessages);
  const [supportInput, setSupportInput] = useState('');
  const [supportPhoto, setSupportPhoto] = useState(null);
  const [supportPhotoPreviewUrl, setSupportPhotoPreviewUrl] = useState(null);
  const [supportSending, setSupportSending] = useState(false);
  const supportPollRef = useRef(null);
  const supportFileInputRef = useRef(null);
  const supportMessagesRef = useRef(supportMessages);
  const supportChatOpenRef = useRef(supportChatOpen);
  const hydratedSessionRef = useRef(null);
  supportMessagesRef.current = supportMessages;
  supportChatOpenRef.current = supportChatOpen;

  useEffect(() => {
    if (!effectiveSessionId) return;
    if (hydratedSessionRef.current === effectiveSessionId) return;
    hydratedSessionRef.current = effectiveSessionId;
    const stored = loadSupportMessages(effectiveSessionId);
    setSupportMessages(stored);
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/support/replies?session_id=${encodeURIComponent(effectiveSessionId)}&undelivered=0&mark_delivered=0`
        );
        const data = await res.json();
        if (!data.success || !data.replies?.length) return;
        setSupportMessages((current) => {
          const merged = mergeSupportMessages(
            current,
            data.replies.map((r) => ({ role: 'support', text: r.text, id: r.id }))
          );
          saveSupportMessages(effectiveSessionId, merged);
          return merged;
        });
      } catch (e) {
        console.warn('Support history load:', e);
      }
    })();
  }, [effectiveSessionId]);

  useEffect(() => {
    if (!effectiveSessionId) return;
    saveSupportMessages(effectiveSessionId, supportMessages);
  }, [effectiveSessionId, supportMessages]);

  useEffect(() => {
    if (!effectiveSessionId) return;
    const ping = () => {
      fetch(`${API_URL}/api/session/ping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: effectiveSessionId }),
      }).catch(() => {});
    };
    const poll = async () => {
      ping();
      try {
        const res = await fetch(
          `${API_URL}/api/support/replies?session_id=${encodeURIComponent(effectiveSessionId)}&undelivered=1`
        );
        const data = await res.json();
        if (!data.success || !data.replies?.length) return;
        const prev = supportMessagesRef.current;
        const ids = new Set(prev.filter((m) => m.role === 'support').map((m) => m.id));
        const newReplies = data.replies.filter((r) => !ids.has(r.id));
        if (newReplies.length === 0) return;
        setSupportMessages((current) => {
          const currentIds = new Set(current.filter((m) => m.role === 'support').map((m) => m.id));
          const toAdd = newReplies.filter((r) => !currentIds.has(r.id));
          if (toAdd.length === 0) return current;
          const merged = [
            ...current,
            ...toAdd.map((r) => ({ role: 'support', text: r.text, id: r.id })),
          ];
          saveSupportMessages(effectiveSessionId, merged);
          return merged;
        });
        if (!supportChatOpenRef.current) {
          setSupportChatOpen(true);
        }
      } catch (e) {
        console.warn('Support replies poll:', e);
      }
    };
    poll();
    supportPollRef.current = setInterval(poll, 4000);
    return () => {
      if (supportPollRef.current) clearInterval(supportPollRef.current);
    };
  }, [effectiveSessionId]);

  const handleSupportSend = async () => {
    const text = supportInput.trim();
    if ((!text && !supportPhoto) || !effectiveSessionId || supportSending) return;
    const photoToSend = supportPhoto;
    const previewUrl = supportPhoto ? URL.createObjectURL(supportPhoto) : null;
    setSupportSending(true);
    setSupportMessages((prev) => {
      const next = [...prev, { role: 'user', text: text || (photoToSend ? '📷 Photo' : ''), photoUrl: previewUrl }];
      saveSupportMessages(effectiveSessionId, next);
      return next;
    });
    setSupportInput('');
    setSupportPhoto(null);
    setSupportPhotoPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    try {
      let res;
      if (photoToSend) {
        const form = new FormData();
        form.append('session_id', effectiveSessionId);
        form.append('text', text);
        form.append('photo', photoToSend);
        res = await fetch(`${API_URL}/api/support/send`, { method: 'POST', body: form });
      } else {
        res = await fetch(`${API_URL}/api/support/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: effectiveSessionId, text }),
        });
      }
      const data = await res.json();
      if (!data.success) {
        setSupportMessages((prev) => [...prev, { role: 'system', text: 'Sorry, we could not send your message. Please try again.' }]);
      }
    } catch (e) {
      setSupportMessages((prev) => [...prev, { role: 'system', text: 'Network error. Please try again.' }]);
    } finally {
      setSupportSending(false);
    }
  };

  const handleSupportPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (supportPhotoPreviewUrl) URL.revokeObjectURL(supportPhotoPreviewUrl);
    setSupportPhotoPreviewUrl(URL.createObjectURL(file));
    setSupportPhoto(file);
    e.target.value = '';
  };

  const clearSupportPhoto = () => {
    if (supportPhotoPreviewUrl) URL.revokeObjectURL(supportPhotoPreviewUrl);
    setSupportPhoto(null);
    setSupportPhotoPreviewUrl(null);
  };

  return (
    <div className="support-chat-wrapper">
      {supportChatOpen && (
        <div className="support-chat-window">
          <div className="support-chat-header">
            <span className="support-chat-dot"></span>
            <span className="support-chat-title">Support Chat</span>
            <button type="button" className="support-chat-close" onClick={() => setSupportChatOpen(false)} aria-label="Close">×</button>
          </div>
          <div className="support-chat-messages">
            {supportMessages.map((m, i) => {
              const isFirstSystem = i === 0 && m.role === 'system';
              const displayText = isFirstSystem && countryCode ? getSupportGreeting(countryCode) : m.text;
              return (
                <div key={m.id || i} className={`support-msg support-msg-${m.role}`}>
                  {m.photoUrl && <img src={m.photoUrl} alt="" className="support-msg-photo" />}
                  {displayText && <span>{displayText}</span>}
                </div>
              );
            })}
          </div>
          {supportPhoto && supportPhotoPreviewUrl && (
            <div className="support-chat-photo-preview">
              <img src={supportPhotoPreviewUrl} alt="" />
              <button type="button" className="support-chat-photo-remove" onClick={clearSupportPhoto} aria-label="Remove photo">×</button>
            </div>
          )}
          <div className="support-chat-input-row">
            <input type="file" ref={supportFileInputRef} accept="image/*" className="support-chat-file-input" onChange={handleSupportPhotoChange} aria-hidden="true" tabIndex={-1} />
            <button type="button" className="support-chat-attach" onClick={() => supportFileInputRef.current?.click()} disabled={supportSending || !effectiveSessionId} aria-label="Attach photo">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
            </button>
            <input
              type="text"
              className="support-chat-input"
              placeholder="Your message..."
              value={supportInput}
              onChange={(e) => setSupportInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSupportSend()}
              disabled={supportSending || !effectiveSessionId}
            />
            <button type="button" className="support-chat-send" onClick={handleSupportSend} disabled={supportSending || (!supportInput.trim() && !supportPhoto) || !effectiveSessionId} aria-label="Send">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
            </button>
          </div>
        </div>
      )}
      <button type="button" className="support-chat-toggle" onClick={() => setSupportChatOpen((o) => !o)} aria-label="Support Chat">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
      </button>
    </div>
  );
}
